import { z } from 'zod/v4';

import { MIN_CHECKOUT_SOURCE_QUANTITY } from '@/features/organizations/billing-constants';
import { api } from '@/lib/api';

export const socialAccountProviderSchema = z.enum(['instagram', 'x']);
export const socialAccountConnectSourceSchema = z.enum(['onboarding', 'social_accounts']);

export const socialAccountAuthorizationSchema = z.object({
    id: z.string(),
    userId: z.string(),
    providerUsername: z.string().nullable(),
    authorizationStatus: z.string(),
    credentialStatus: z.string(),
    grantedScopes: z.array(z.string()),
    isDeleted: z.boolean(),
    lastValidatedAt: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    revokedAt: z.string().nullable()
});

export const socialAccountStatusSchema = z.enum([
    'active',
    'degraded',
    'reauthorization_required',
    'disconnected',
    'error'
]);

export const socialAccountCapabilitiesSchema = z.object({
    publishing: z.boolean(),
    insights: z.boolean()
});

export const socialAccountSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    provider: socialAccountProviderSchema,
    targetType: z.string(),
    externalTargetId: z.string(),
    displayName: z.string(),
    handleOrUsername: z.string().nullable(),
    profileUrl: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    status: socialAccountStatusSchema,
    grantedScopes: z.array(z.string()),
    authorizations: z.array(socialAccountAuthorizationSchema),
    activeAuthorization: socialAccountAuthorizationSchema.nullable(),
    capabilities: socialAccountCapabilitiesSchema,
    connectedAt: z.string(),
    lastUsedAt: z.string().nullable().optional()
});

export const socialAccountConnectStartResponseSchema = z.object({
    authorizationUrl: z.string().url(),
    state: z.string(),
    callbackUrl: z.string().url(),
    scopes: z.array(z.string())
});

export const socialAccountSourceLimitSchema = z
    .object({
        used: z.number().int().min(0),
        limit: z.number().int().min(0).nullable().default(null),
        canConnect: z.boolean(),
        recommendedCheckoutQuantity: z.number().int().min(MIN_CHECKOUT_SOURCE_QUANTITY).optional()
    })
    .transform((sourceLimit) => ({
        ...sourceLimit,
        recommendedCheckoutQuantity:
            sourceLimit.recommendedCheckoutQuantity ?? Math.max(MIN_CHECKOUT_SOURCE_QUANTITY, sourceLimit.used)
    }));

export type SocialAccountProvider = z.infer<typeof socialAccountProviderSchema>;
export type SocialAccountConnectSource = z.infer<typeof socialAccountConnectSourceSchema>;
export type SocialAccountAuthorization = z.infer<typeof socialAccountAuthorizationSchema>;
export type SocialAccountStatus = z.infer<typeof socialAccountStatusSchema>;
export type SocialAccountCapabilities = z.infer<typeof socialAccountCapabilitiesSchema>;
export type SocialAccount = z.infer<typeof socialAccountSchema>;
export type SocialAccountConnectStartResponse = z.infer<typeof socialAccountConnectStartResponseSchema>;
export type SocialAccountSourceLimit = z.infer<typeof socialAccountSourceLimitSchema>;

export async function listOrganizationSocialAccounts(organizationId: string): Promise<SocialAccount[]> {
    const response = await api.get(`/v1/organizations/${organizationId}/social-accounts`, { authenticated: true });
    return z.array(socialAccountSchema).parse(response);
}

export async function getOrganizationSocialAccountSourceLimit(
    organizationId: string
): Promise<SocialAccountSourceLimit> {
    const response = await api.get(`/v1/organizations/${organizationId}/social-accounts/source-limit`, {
        authenticated: true
    });
    return socialAccountSourceLimitSchema.parse(response);
}

export async function startSocialAccountConnect(args: {
    organizationId: string;
    provider: SocialAccountProvider;
    successRedirectUrl?: string;
    errorRedirectUrl?: string;
    source?: SocialAccountConnectSource;
}): Promise<SocialAccountConnectStartResponse> {
    const body = {
        ...(args.successRedirectUrl ? { successRedirectUrl: args.successRedirectUrl } : {}),
        ...(args.errorRedirectUrl ? { errorRedirectUrl: args.errorRedirectUrl } : {}),
        ...(args.source ? { source: args.source } : {})
    };
    const payload = Object.keys(body).length > 0 ? body : undefined;

    const response = await api.post(
        `/v1/organizations/${args.organizationId}/social-accounts/${args.provider}/connect`,
        payload,
        {
            authenticated: true
        }
    );

    return socialAccountConnectStartResponseSchema.parse(response);
}

export async function disconnectSocialAccountAuthorization(args: {
    organizationId: string;
    socialAccountId: string;
    authorizationId: string;
}): Promise<SocialAccount> {
    const response = await api.delete(
        `/v1/organizations/${args.organizationId}/social-accounts/${args.socialAccountId}/authorizations/${args.authorizationId}`,
        { authenticated: true }
    );

    return socialAccountSchema.parse(response);
}

export async function reconnectSocialAccountAuthorization(args: {
    organizationId: string;
    socialAccountId: string;
    authorizationId: string;
}): Promise<SocialAccountConnectStartResponse> {
    const response = await api.post(
        `/v1/organizations/${args.organizationId}/social-accounts/${args.socialAccountId}/authorizations/${args.authorizationId}/reconnect`,
        undefined,
        { authenticated: true }
    );

    return socialAccountConnectStartResponseSchema.parse(response);
}
